import type { AccessType, GetSponsorInfoResponse, UpdateAccessSettingsParams } from '@/services/api/api.types'

/** Where guests are sent when they reach their interaction limit. */
export type GuestDestination = 'trial' | 'external' | 'register'

export interface AccessSettings {
  trialsEnabled: boolean
  /** The external page users subscribe on when a trial ends. Stored as the channel's subscription URL. */
  paymentUrl: string
  inviteDefaultAccess: AccessType
  selfRegEnabled: boolean
  selfRegAccess: AccessType
  guestAccess: boolean
  guestDestination: GuestDestination
  guestPaymentUrl: string
  /** No guest destination has been saved since C2-2824, so guests get the app's old Sign up / Request Access. */
  guestDestinationUnset?: boolean
  discoverable: boolean
}

// A backend without C2-2817 (test and prod until it is released) only has trials through self-registration.
export function getTrialsEnabled(info: GetSponsorInfoResponse): boolean {
  return info.trialsEnabled ?? info.selfRegistrationTrialAccount ?? false
}

const isRegistrationUrl = (url: string, registrationUrl: string) => {
  try {
    return new URL(url).pathname === new URL(registrationUrl).pathname
  } catch {
    return false
  }
}

/**
 * What the backend holds. The self-registration form is stored as the channel's registration link in
 * registration_url (C2-2824 has no mode for it), so it is recognised by the link's path, whichever portal
 * host saved it.
 */
export function readStoredSettings(info: GetSponsorInfoResponse, registrationUrl: string): AccessSettings {
  const url = info.registrationUrl ?? ''
  const useTrial = info.registrationUrlUseTrial
  const guest: Pick<AccessSettings, 'guestDestination' | 'guestPaymentUrl' | 'guestDestinationUnset'> =
    useTrial !== false
      ? { guestDestination: 'trial', guestPaymentUrl: '', ...(useTrial == null && { guestDestinationUnset: true }) }
      : registrationUrl && isRegistrationUrl(url, registrationUrl)
        ? { guestDestination: 'register', guestPaymentUrl: '' }
        : { guestDestination: 'external', guestPaymentUrl: url }
  return {
    trialsEnabled: getTrialsEnabled(info),
    paymentUrl: info.subscriptionUrl ?? '',
    inviteDefaultAccess: info.manualInviteAccessType === 'free_trial' ? 'trial' : 'channel',
    selfRegEnabled: info.selfRegistrationEnabled ?? false,
    selfRegAccess: info.selfRegistrationTrialAccount ? 'trial' : 'channel',
    guestAccess: info.guestUserAccess ?? false,
    ...guest,
    discoverable: info.discoverable ?? false,
  }
}

/**
 * What the page shows: the stored settings, with the rules between them applied. A channel with no saved
 * guest destination shows the one matching what its guests get today (Sign up while self-registration is on,
 * else Request Access to the payment page), and the next save with guests on stores it.
 */
export function fromSponsorInfo(info: GetSponsorInfoResponse, registrationUrl: string): AccessSettings {
  const { guestDestinationUnset, ...stored } = readStoredSettings(info, registrationUrl)
  if (guestDestinationUnset) return normalize({ ...stored, guestDestination: defaultGuestDestination(stored) })
  // On the trial page with trials off: show where guests go today as a page of its own, if they go anywhere.
  const current = info.effectiveRegistrationUrl ?? info.subscriptionUrl ?? ''
  if (stored.guestDestination === 'trial' && !stored.trialsEnabled && current)
    return normalize({ ...stored, guestDestination: 'external', guestPaymentUrl: current })
  return normalize(stored)
}

export function isValidPaymentUrl(value: string): boolean {
  try {
    const url = new URL(value.trim())
    return url.protocol === 'http:' || url.protocol === 'https:'
  } catch {
    return false
  }
}

function isGuestDestinationChanged(saved: AccessSettings, next: AccessSettings): boolean {
  return (
    next.guestDestination !== saved.guestDestination ||
    (next.guestDestination === 'external' && next.guestPaymentUrl.trim() !== saved.guestPaymentUrl.trim())
  )
}

const isGuestChanged = (saved: AccessSettings, next: AccessSettings) =>
  next.guestAccess !== saved.guestAccess || isGuestDestinationChanged(saved, next)

const isTrialsPageMissing = (settings: AccessSettings) =>
  settings.trialsEnabled && !isValidPaymentUrl(settings.paymentUrl)

const isTrialsPageChanged = (saved: AccessSettings, next: AccessSettings) =>
  !saved.trialsEnabled || next.paymentUrl.trim() !== saved.paymentUrl.trim()

const isGuestPageMissing = (settings: AccessSettings) =>
  settings.guestAccess && settings.guestDestination === 'external' && !isValidPaymentUrl(settings.guestPaymentUrl)

/**
 * The URL fields holding Save back, in page order. A page left missing by a channel that predates the rule
 * (trials backfilled on, guests before guest destinations) only holds Save back once the owner touches it,
 * as the backend does.
 */
export function missingUrls(settings: AccessSettings, saved?: AccessSettings): Array<'paymentUrl' | 'guestPaymentUrl'> {
  const missing: Array<'paymentUrl' | 'guestPaymentUrl'> = []
  if (isTrialsPageMissing(settings) && (!saved || isTrialsPageChanged(saved, settings))) missing.push('paymentUrl')
  if (isGuestPageMissing(settings) && (!saved || isGuestChanged(saved, settings))) missing.push('guestPaymentUrl')
  return missing
}

export function canSave(settings: AccessSettings, saved?: AccessSettings): boolean {
  return missingUrls(settings, saved).length === 0
}

export function isGuestDestinationAvailable(settings: AccessSettings, destination: GuestDestination): boolean {
  if (destination === 'trial') return settings.trialsEnabled
  if (destination === 'register') return settings.selfRegEnabled
  return true
}

export function defaultGuestDestination(settings: AccessSettings): GuestDestination {
  if (settings.selfRegEnabled) return 'register'
  if (settings.trialsEnabled) return 'trial'
  return 'external'
}

/**
 * Settings that depend on another one follow it: Free trial needs trials on, Discoverable needs
 * self-registration, guests need a destination that's on offer.
 */
function normalize(settings: AccessSettings): AccessSettings {
  const next = { ...settings }
  if (!next.trialsEnabled) {
    next.inviteDefaultAccess = 'channel'
    next.selfRegAccess = 'channel'
  }
  if (!next.selfRegEnabled) next.discoverable = false
  if (!isGuestDestinationAvailable(next, next.guestDestination)) next.guestDestination = defaultGuestDestination(next)
  // Guests left with no page of their own go where the app sends them today: Request Access on the payment page.
  if (next.guestDestination === 'external' && !next.guestPaymentUrl.trim())
    next.guestPaymentUrl = next.paymentUrl.trim()
  return next
}

/** Free trial is only an option while trials are on; while they are, self-registered users must start on one. */
export function setTrialsEnabled(settings: AccessSettings, trialsEnabled: boolean): AccessSettings {
  const access: AccessType = trialsEnabled ? 'trial' : 'channel'
  return normalize({ ...settings, trialsEnabled, inviteDefaultAccess: access, selfRegAccess: access })
}

export function setSelfRegEnabled(settings: AccessSettings, selfRegEnabled: boolean): AccessSettings {
  return normalize({
    ...settings,
    selfRegEnabled,
    selfRegAccess: settings.trialsEnabled ? 'trial' : settings.selfRegAccess,
  })
}

/** Switching guest access back on restores the saved destination, else picks the default. */
export function setGuestAccess(settings: AccessSettings, guestAccess: boolean, saved?: AccessSettings): AccessSettings {
  if (!guestAccess) return { ...settings, guestAccess }
  if (saved?.guestAccess)
    return normalize({
      ...settings,
      guestAccess,
      guestDestination: saved.guestDestination,
      guestPaymentUrl: saved.guestPaymentUrl,
    })
  return normalize({ ...settings, guestAccess, guestDestination: defaultGuestDestination(settings) })
}

export function isAccessTypeAvailable(settings: AccessSettings, kind: 'invite' | 'selfReg', type: AccessType) {
  if (type === 'trial') return settings.trialsEnabled
  return kind === 'invite' || !settings.trialsEnabled
}

function guestParams(settings: AccessSettings, registrationUrl: string): UpdateAccessSettingsParams {
  if (settings.guestDestination === 'trial') return { registration_url_use_trial: true }
  const url = settings.guestDestination === 'register' ? registrationUrl : settings.guestPaymentUrl.trim()
  return { registration_url_use_trial: false, registration_url: url || null }
}

/**
 * The changes to send, diffed against what the backend stores (readStoredSettings), so corrections the page
 * made on load are saved too. A guest destination is only sent while guests are on and once it is complete.
 */
export function toUpdateParams(
  stored: AccessSettings,
  next: AccessSettings,
  { registrationUrl = '' } = {},
): UpdateAccessSettingsParams {
  const params: UpdateAccessSettingsParams = {}
  const paymentUrl = next.paymentUrl.trim()
  if (paymentUrl !== stored.paymentUrl) params.subscription_url = paymentUrl || null
  if (next.trialsEnabled !== stored.trialsEnabled) params.trials_enabled = next.trialsEnabled
  if (next.inviteDefaultAccess !== stored.inviteDefaultAccess)
    params.manual_invite_access_type = next.inviteDefaultAccess === 'trial' ? 'free_trial' : 'paid_by_channel'
  if (next.selfRegEnabled !== stored.selfRegEnabled) params.self_registration_enabled = next.selfRegEnabled
  if (next.selfRegAccess !== stored.selfRegAccess)
    params.self_registration_trial_account = next.selfRegAccess === 'trial'
  if (next.guestAccess !== stored.guestAccess) params.guest_user_access = next.guestAccess
  const guestPageMissing =
    next.guestDestination === 'external'
      ? !isValidPaymentUrl(next.guestPaymentUrl)
      : next.guestDestination === 'register' && !registrationUrl
  if (
    next.guestAccess &&
    (stored.guestDestinationUnset || isGuestDestinationChanged(stored, next)) &&
    !guestPageMissing
  )
    Object.assign(params, guestParams(next, registrationUrl))
  if (next.discoverable !== stored.discoverable) params.discoverable = next.discoverable
  return params
}

export function isSameSettings(a: AccessSettings, b: AccessSettings): boolean {
  return (
    a.trialsEnabled === b.trialsEnabled &&
    a.paymentUrl.trim() === b.paymentUrl.trim() &&
    a.inviteDefaultAccess === b.inviteDefaultAccess &&
    a.selfRegEnabled === b.selfRegEnabled &&
    a.selfRegAccess === b.selfRegAccess &&
    a.discoverable === b.discoverable &&
    !isGuestChanged(a, b)
  )
}
